// Email Intelligence tab. Owner-facing review surface for what Jarvis
// triaged overnight. Five sections:
//   1. Summary bar — today's counts
//   2. Action Required — flagged items
//   3. Drafts Ready for Review — pending drafts
//   4. Jarvis Learned Today — extracted facts
//   5. (collapsed) Handled silently — count of noise

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { JarvisCard } from '@/components/JarvisCard'
import { EmailSummaryBar } from '@/components/EmailSummaryBar'
import { EmailDraftCard } from '@/components/EmailDraftCard'
import { EmailFlagCard } from '@/components/EmailFlagCard'
import { EmailLearnedSection } from '@/components/EmailLearnedSection'
import { apiClient, unwrap } from '@/lib/apiClient'
import { HAS_AUTH } from '@/lib/auth'
import { useConnections } from '@/lib/hooks'
import { useToast } from '@/components/Toast'
import type {
  EmailDraftListItem,
  EmailFactItem,
  TimeSavingsAggregate,
} from '@/types'
import { useMutation } from '@tanstack/react-query'

interface TriageNowResponse {
  attempted: number
  processed: number
  drafted: number
  flagged: number
  ignored: number
  failed: number
}

const MAX_FLAGGED_VISIBLE = 5

export function Email() {
  const qc = useQueryClient()
  const toast = useToast()
  const [showHandled, setShowHandled] = useState(false)

  const connections = useConnections()
  const googleConn = connections.data?.items.find((c) => c.provider === 'google')
  const connected = googleConn?.status === 'connected'

  const triage = useMutation({
    // Triage runs N Claude calls sequentially (~5-15s each) plus Gmail draft
    // writes — bumps well past the default 30s axios timeout. 3 min covers
    // ~15 emails comfortably; the Azure Function itself caps at 230s.
    mutationFn: () =>
      unwrap<TriageNowResponse>(
        apiClient.post('/email/triage-now', undefined, { timeout: 180_000 }),
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['email-drafts'] })
      qc.invalidateQueries({ queryKey: ['email-time-savings'] })
      qc.invalidateQueries({ queryKey: ['email-facts'] })
      qc.invalidateQueries({ queryKey: ['connections'] })
      if (data.attempted === 0) {
        toast.success('No unread mail to triage')
      } else if (data.processed === 0) {
        toast.error(
          `Triage failed on all ${data.attempted} message${data.attempted === 1 ? '' : 's'}`,
          'Check API logs for the underlying error.',
        )
      } else {
        const summary = `${data.drafted} drafted · ${data.flagged} flagged · ${data.ignored} ignored${
          data.failed > 0 ? ` · ${data.failed} errored` : ''
        }`
        toast.success(
          `Triaged ${data.processed} of ${data.attempted}`,
          summary,
        )
      }
    },
    onError: (e) =>
      toast.error('Triage failed', (e as Error).message),
  })

  const drafts = useQuery<{ items: EmailDraftListItem[] }>({
    queryKey: ['email-drafts'],
    queryFn: () =>
      unwrap<{ items: EmailDraftListItem[] }>(apiClient.get('/email/drafts')),
    enabled: HAS_AUTH && connected,
    refetchInterval: 60_000,
  })

  const timeSavings = useQuery<TimeSavingsAggregate>({
    queryKey: ['email-time-savings', 'today'],
    queryFn: () =>
      unwrap<TimeSavingsAggregate>(
        apiClient.get('/email/time-savings', { params: { period: 'today' } }),
      ),
    enabled: HAS_AUTH && connected,
    refetchInterval: 120_000,
  })

  const facts = useQuery<{ items: EmailFactItem[] }>({
    queryKey: ['email-facts', 'today'],
    queryFn: () =>
      unwrap<{ items: EmailFactItem[] }>(
        apiClient.get('/email/facts', { params: { period: 'today' } }),
      ),
    enabled: HAS_AUTH && connected,
    refetchInterval: 120_000,
  })

  const onChanged = () => {
    qc.invalidateQueries({ queryKey: ['email-drafts'] })
    qc.invalidateQueries({ queryKey: ['email-time-savings'] })
  }

  if (connections.isLoading) {
    return (
      <div className="space-y-6 max-w-4xl">
        <h1 className="text-2xl font-semibold tracking-tight">Email</h1>
        <p className="text-jarvis-muted text-sm">Loading…</p>
      </div>
    )
  }

  if (!connected) {
    return <EmptyState />
  }

  const items = drafts.data?.items ?? []
  const flagged = items.filter((i) => i.isFlag).slice(0, MAX_FLAGGED_VISIBLE)
  const draftItems = items.filter((i) => !i.isFlag)
  const handledCount = timeSavings.data?.breakdown.email_auto_handled
    ? Math.round(timeSavings.data.breakdown.email_auto_handled / 3) // 3 min per auto-handled
    : 0

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Email</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => triage.mutate()}
            disabled={triage.isPending}
            className="text-xs px-3 py-1.5 bg-jarvis-surface-2 border border-jarvis-border rounded-lg hover:bg-jarvis-blue/10 hover:border-jarvis-blue/40 hover:text-jarvis-blue transition-colors flex items-center gap-1.5 disabled:opacity-50"
            title="Pull unread mail from Gmail now and triage with AI"
          >
            <RefreshCw size={12} className={triage.isPending ? 'animate-spin' : ''} />
            {triage.isPending ? 'Triaging…' : 'Run inbox triage now'}
          </button>
          <div className="text-xs text-jarvis-muted">
            Last verified:{' '}
            {googleConn?.lastVerifiedAt
              ? new Date(googleConn.lastVerifiedAt).toLocaleString()
              : 'never'}
          </div>
        </div>
      </div>

      <EmailSummaryBar
        totals={timeSavings.data}
        draftsReady={draftItems.length}
        flaggedCount={flagged.length}
      />

      {flagged.length > 0 && (
        <Section title="Action required" count={flagged.length}>
          {flagged.map((item) => (
            <EmailFlagCardWrapper key={item.id} item={item} onChanged={onChanged} />
          ))}
        </Section>
      )}

      {draftItems.length > 0 && (
        <Section title="Drafts ready for review" count={draftItems.length}>
          {draftItems.map((item) => (
            <EmailDraftCard key={item.id} item={item} onChanged={onChanged} />
          ))}
        </Section>
      )}

      {flagged.length === 0 && draftItems.length === 0 && (
        <JarvisCard>
          <div className="text-center py-6">
            <div className="text-3xl mb-2">📬</div>
            <p className="text-sm text-jarvis-text">Inbox zero on your end.</p>
            <p className="text-xs text-jarvis-muted mt-1">
              Nothing needs your eye right now. Jarvis will run again before tomorrow's briefing.
            </p>
          </div>
        </JarvisCard>
      )}

      {facts.data && <EmailLearnedSection facts={facts.data.items} />}

      {handledCount > 0 && (
        <JarvisCard>
          <button
            onClick={() => setShowHandled((s) => !s)}
            className="w-full flex items-center justify-between text-left"
          >
            <span className="text-xs uppercase tracking-widest text-jarvis-muted">
              Handled silently · {handledCount} email{handledCount === 1 ? '' : 's'}
            </span>
            <span className="text-xs text-jarvis-muted">
              {showHandled ? '▲' : '▼'}
            </span>
          </button>
          {showHandled && (
            <p className="text-xs text-jarvis-muted mt-2">
              Newsletters, automated receipts, social notifications. Labeled
              <span className="font-mono text-jarvis-text mx-1">Jarvis/Processed</span>
              in Gmail and marked read. Open Gmail directly if you want to see them.
            </p>
          )}
        </JarvisCard>
      )}
    </div>
  )
}

function Section({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-medium text-jarvis-text">
          {title} <span className="text-jarvis-muted">· {count}</span>
        </h2>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  )
}

function EmailFlagCardWrapper({
  item,
  onChanged,
}: {
  item: EmailDraftListItem
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const skip = useMutation({
    mutationFn: () => unwrap(apiClient.post(`/email/drafts/${item.id}/skip`)),
    onSuccess: () => {
      onChanged()
      qc.invalidateQueries({ queryKey: ['email-drafts'] })
    },
  })
  return (
    <EmailFlagCard item={item} onSkip={() => skip.mutate()} skipping={skip.isPending} />
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-6 text-center max-w-2xl mx-auto">
      <span className="text-5xl">📬</span>
      <h2 className="text-xl font-semibold text-jarvis-text">
        Your inbox is costing you about 14 hours a week.
      </h2>
      <p className="text-sm text-jarvis-muted max-w-md">
        Connect your Google account and Jarvis reads your inbox for you —
        triaging overnight, drafting replies for your review, and surfacing
        only what needs your eye. It never sends without your approval.
      </p>
      <Link
        to="/setup"
        className="bg-jarvis-blue hover:bg-jarvis-blue-bright px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2"
      >
        <Mail size={16} />
        Connect Google in Setup
      </Link>
      <p className="text-xs text-jarvis-muted">
        You can disconnect any time.
      </p>
    </div>
  )
}
